"use client";

import { c as _c } from "react/compiler-runtime";
import { IconLayoutDashboard, IconLogout, IconMoon, IconSettings, IconSun } from "@tabler/icons-react";
import { getImageProps } from "next/image";
import Link from "next/link";
import { useRouter } from "@/hooks/use-router";
import { useTheme } from "@/hooks/use-theme";
import { useAnalytics } from "@/lib/analytics/client";
import { signOut, useSession } from "@domainstack/auth/client";
import { Avatar, AvatarFallback, AvatarImage } from "@domainstack/ui/avatar";
import { Button } from "@domainstack/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@domainstack/ui/dropdown-menu";
export function UserMenu() {
  const $ = _c(53);
  const router = useRouter();
  const analytics = useAnalytics();
  const {
    data: session
  } = useSession();
  const {
    theme,
    toggleTheme
  } = useTheme();
  if (!session?.user) {
    return null;
  }
  const {
    user
  } = session;
  const avatarUrl = `/api/avatar/${user.id}`;
  const t0 = user.name ?? "User avatar";
  let t1;
  if ($[0] !== avatarUrl || $[1] !== t0) {
    t1 = getImageProps({
      src: avatarUrl,
      alt: t0,
      width: 32,
      height: 32,
      loading: "eager",
      draggable: false
    });
    $[0] = avatarUrl;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    props: imageProps
  } = t1;
  let t2;
  if ($[3] !== user.name) {
    t2 = (user.name || "").split(" ").filter(_temp).map(_temp2).join("").toUpperCase().slice(0, 2) || "?";
    $[3] = user.name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const initials = t2;
  let t3;
  if ($[5] !== analytics || $[6] !== router) {
    t3 = async () => {
      analytics.track("sign_out_clicked");
      try {
        await signOut({
          fetchOptions: {
            onSuccess: () => {
              router.push("/");
            }
          }
        });
      } catch {}
    };
    $[5] = analytics;
    $[6] = router;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleSignOut = t3;
  let t4;
  if ($[8] !== imageProps) {
    t4 = <AvatarImage {...imageProps} />;
    $[8] = imageProps;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== initials) {
    t5 = <AvatarFallback className="bg-muted text-xs text-muted-foreground">{initials}</AvatarFallback>;
    $[10] = initials;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <DropdownMenuTrigger render={<Button variant="ghost" size="icon" className="rounded-full px-0 py-0 hover:bg-transparent active:scale-95 dark:hover:bg-transparent" aria-label="User menu"><Avatar className="size-8">{t4}{t5}</Avatar></Button>} />;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== imageProps) {
    t7 = <AvatarImage {...imageProps} />;
    $[15] = imageProps;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== initials) {
    t8 = <AvatarFallback className="bg-muted text-xs text-muted-foreground">{initials}</AvatarFallback>;
    $[17] = initials;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <Avatar className="size-8">{t7}{t8}</Avatar>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = user.name || "User";
  let t11;
  if ($[22] !== t10) {
    t11 = <p className="text-sm leading-none font-medium">{t10}</p>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== user.email) {
    t12 = <p className="text-xs leading-none text-muted-foreground">{user.email}</p>;
    $[24] = user.email;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = <div className="flex flex-col space-y-[5px]">{t11}{t12}</div>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t9) {
    t14 = <div className="flex items-center gap-[9px] p-1.5 select-none">{t9}{t13}</div>;
    $[29] = t13;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DropdownMenuSeparator />;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <DropdownMenuItem nativeButton={false} render={<Link href="/dashboard"><IconLayoutDashboard />Dashboard</Link>} />;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <DropdownMenuItem nativeButton={false} render={<Link href="/settings" scroll={false}><IconSettings />Settings</Link>} />;
    t18 = <DropdownMenuSeparator />;
    $[34] = t17;
    $[35] = t18;
  } else {
    t17 = $[34];
    t18 = $[35];
  }
  let t19;
  if ($[36] !== theme) {
    t19 = theme === "dark" ? <IconSun /> : <IconMoon />;
    $[36] = theme;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = theme === "dark" ? "Light mode" : "Dark mode";
  let t21;
  if ($[38] !== t19 || $[39] !== t20 || $[40] !== toggleTheme) {
    t21 = <DropdownMenuItem onClick={toggleTheme} closeOnClick={false}>{t19}{t20}</DropdownMenuItem>;
    $[38] = t19;
    $[39] = t20;
    $[40] = toggleTheme;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <DropdownMenuSeparator />;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <IconLogout className="text-danger-foreground" />;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== handleSignOut) {
    t24 = <DropdownMenuItem onClick={handleSignOut}>{t23}Sign out</DropdownMenuItem>;
    $[44] = handleSignOut;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t14 || $[47] !== t21 || $[48] !== t24) {
    t25 = <DropdownMenuContent align="end" className="min-w-56">{t14}{t15}{t16}{t17}{t18}{t21}{t22}{t24}</DropdownMenuContent>;
    $[46] = t14;
    $[47] = t21;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t25 || $[51] !== t6) {
    t26 = <DropdownMenu modal={false}>{t6}{t25}</DropdownMenu>;
    $[50] = t25;
    $[51] = t6;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  return t26;
}
function _temp2(n_0) {
  return n_0[0];
}
function _temp(n) {
  return n.length > 0;
}